import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useGetSingleRecordQuery, useGetMarketDataMutation, useRecalcScoreQuery } from '../../../services/Listing';
import { mapListing } from '../../../utils/mapListing';
import { resolveSignals } from '../utils/signals';
import { buildBaseline } from '../utils/dealCalc';
import { useRules } from '../../../hooks/useRules';

/**
 * Approximate subscores from baseline data.
 * Used as fallback when recalc endpoint is not available.
 * Will be removed once backend is deployed with /recalc endpoint.
 */
function approximateSubscores(baseline, signals) {
  // Deal score (0-40): based on market position
  let deal = 18;
  const over = baseline?.marketOverMarket;
  if (over != null) {
    if (over < -20) deal += 22;
    else if (over < -10) deal += 17;
    else if (over < 0) deal += 11;
    else if (over < 10) deal += 5;
  }
  if (baseline?.faktor) {
    if (baseline.faktor < 20) deal += 5;
    else if (baseline.faktor > 30) deal -= 5;
  }
  deal = Math.max(0, Math.min(40, deal));

  // Income score (0-30): financing-independent, based on gross yield
  const bruttoRendite = baseline?.bruttoRendite ?? null;
  let income;
  if (bruttoRendite != null) {
    income = bruttoRendite >= 6 ? 30 : bruttoRendite >= 5 ? 26 : bruttoRendite >= 4 ? 21 : bruttoRendite >= 3 ? 16 : bruttoRendite >= 2 ? 10 : 5;
  } else {
    const cf = baseline?.cf?.cashflow ?? 0;
    income = cf > 300 ? 28 : cf > 150 ? 24 : cf > 50 ? 19 : cf > 0 ? 15 : cf > -100 ? 10 : cf > -300 ? 5 : 2;
  }

  // Object score (0-20): based on signals (risk/caution reduce, positive increases)
  const risks = signals?.filter(s => s.type === 'risk' || s.type === 'caution') || [];
  const positives = signals?.filter(s => s.type === 'positive') || [];
  let object = Math.max(0, Math.min(20, 17 - risks.length * 3 + positives.length));

  // Location score (0-10): simplified fallback
  let location = 6;

  return {
    deal:     { score: deal,     max: 40, color: '#1DB3B3', label: 'Deal' },
    income:   { score: income,   max: 30, color: '#0E7A6B', label: 'Ertrag' },
    object:   { score: object,   max: 20, color: '#3FC0C0', label: 'Objekt' },
    location: { score: location, max: 10, color: '#0B5C52', label: 'Lage' },
  };
}

/**
 * Loads listing data. Tries the live recalc endpoint first.
 * Falls back to old getSingleRecord + market data if recalc is unavailable.
 */
export function useDealData(listingId) {
  const auth = useAuth();
  const userID = auth.user?.profile?.sub;
  const rules = useRules();
  const [market, setMarket] = useState(null);
  const [getMarketData] = useGetMarketDataMutation();

  // Try recalc endpoint
  const { data: recalcData, isError: recalcError } = useRecalcScoreQuery(
    listingId,
    { skip: !userID || !listingId }
  );

  // Fallback: old endpoint
  const useOldEndpoint = recalcError || !recalcData;
  const { data: recordData, isLoading: oldLoading, isError: oldError } = useGetSingleRecordQuery(
    { UserID: userID, ListingID: listingId },
    { skip: !userID || !listingId || !!recalcData }
  );

  const raw = recalcData ? recalcData.listing : recordData?.data;
  const isLoading = !recalcData && oldLoading;
  const isError = recalcError && (oldError || (!oldLoading && !recordData?.success));

  // Fetch market data for old endpoint path
  useEffect(() => {
    if (!raw || recalcData) return; // skip if recalc provided market data
    const type = raw.PropertyType === 'Wohnung' || raw.PropertyType === 'Apartment' ? 'Wohnung' : 'Haus';
    const city = raw.Google_City || raw.City;
    if (!city) return;

    getMarketData({ type, cities: [{ city, region: raw.Google_Region || raw.State }] })
      .unwrap()
      .then(res => {
        const r = res?.results?.[0];
        if (!r?.kaufpreise?.durchschnittskaufpreis) return;
        setMarket({
          avg: r.kaufpreise.durchschnittskaufpreis,
          min: r.kaufpreise.min_kaufpreis_pro_m2,
          max: r.kaufpreise.max_kaufpreis_pro_m2,
          avgRent: r.mieten?.durchschnittsmiete,
          minRent: r.mieten?.min_miete_pro_m2,
          maxRent: r.mieten?.max_miete_pro_m2,
          source: r.quelle,
        });
      })
      .catch(() => {});
  }, [raw, recalcData, getMarketData]);

  if (!raw) {
    return {
      deal: null, raw: null, market: null, signals: [],
      baseline: null, subscores: null, recalc: null,
      isLoading, isError: isError || false,
    };
  }

  // Determine market data source
  const effectiveMarket = recalcData ? {
    avg: recalcData.marketData?.avgPricePerSqm,
    avgRent: recalcData.marketData?.avgRentPerSqm,
    miettrend: recalcData.marketData?.miettrend,
    matchedCity: recalcData.marketData?.matchedCity,
    priceDeviation: recalcData.marketData?.priceDeviation,
  } : market;

  const deal = mapListing(raw);
  deal.raw = raw;

  // Merge: Backend-Signale (strukturiert) + Plugin-Signale (client-side Freitext) — dedupliziert
  const backendKeys = recalcData?.signals?.map(s => s.key || s) || [];
  const dbKeys = deal.signals || [];
  const mergedKeys = [...new Set([...backendKeys, ...dbKeys])];
  const signals = resolveSignals(mergedKeys, rules);

  let baseline;

  if (recalcData?.financing) {
    // Backend ist Wahrheit — alle Werte aus recalcData
    const rc = recalcData.cashflow || {};
    const fin = recalcData.financing;
    const mkt = recalcData.marketData || {};

    baseline = {
      // Objektdaten aus raw
      kaufpreis: parseFloat(raw.PurchasePrice) || 0,
      wohnflaeche: parseFloat(raw.Area) || 0,
      baujahr: raw.YearOfConstruction ? parseInt(raw.YearOfConstruction) : null,
      bundesland: raw.Google_Region || raw.State || '',
      rooms: raw.Rooms || null,
      propertyType: raw.PropertyType || '',
      ppSqm: parseFloat(raw.Area) > 0 ? Math.round(parseFloat(raw.PurchasePrice) / parseFloat(raw.Area)) : null,

      // Finanzierung aus Backend (user_settings-korrekt)
      eigenkapitalAnteil: fin.eigenkapitalAnteil,
      zinssatz: fin.zinssatz,
      tilgungssatz: fin.tilgungssatz,
      tilgungActive: fin.tilgungActive ?? false,    // Toggle-Zustand aus DB
      maklerAnteil: fin.maklerAnteil,
      notarAnteil: fin.notarAnteil,
      nk: fin.nk,
      gesamtkosten: fin.gesamtkosten,
      eigenkapital: fin.eigenkapital,
      darlehensbetrag: fin.darlehensbetrag,
      // Listing-spezifische Overrides für SimulateControls
      grunderwerbSteuerOverride: raw.GrunderwerbSteuer != null ? parseFloat(raw.GrunderwerbSteuer) : null,
      baseRentOverride: raw.BaseRent != null && parseFloat(raw.BaseRent) > 0 ? parseFloat(raw.BaseRent) : null,

      // KPIs
      monatskaltmiete: rc.estimatedRent || 0,
      bruttoRendite: fin.gesamtkosten > 0 ? ((rc.estimatedRent || 0) * 12 / fin.gesamtkosten) * 100 : null,
      ekRendite: rc.ekRendite ?? null,
      faktor: (rc.estimatedRent || 0) > 0 ? Math.round(((parseFloat(raw.PurchasePrice) || 0) / ((rc.estimatedRent || 0) * 12)) * 10) / 10 : null,
      breakEvenZins: null,
      marketOverMarket: mkt.priceDeviation ?? null,
      score: recalcData.score?.total ?? raw.Score ?? null,

      // Cashflow
      cf: rc.estimatedRent ? {
        einnahmen: rc.estimatedRent,
        cashflow: rc.vorTilgung,
        cashflowJahr: rc.jaehrlich ?? (rc.vorTilgung != null ? rc.vorTilgung * 12 : null),
        davonZins: rc.zinsMonatlich,
        davonTilgung: rc.tilgungMonatlich,
        kreditrate: rc.kreditrate ?? ((rc.zinsMonatlich || 0) + (rc.tilgungMonatlich || 0)),
        bewirtschaftung: rc.bewirtschaftung,
        cashflowNachTilgung: rc.nachTilgung,
        cashflowNachTilgungJahr: rc.nachTilgung != null ? rc.nachTilgung * 12 : null,
      } : null,
    };
  } else {
    // Fallback: kein recalcData → lokale Berechnung
    baseline = buildBaseline(raw, effectiveMarket);
  }

  // Subscores: from recalc API or approximated
  let subscores;
  if (recalcData) {
    baseline.score = recalcData.score.total;
    subscores = {
      deal:     { score: recalcData.score.deal,     max: 40, color: '#1DB3B3', label: 'Deal' },
      income:   { score: recalcData.score.income,   max: 30, color: '#0E7A6B', label: 'Ertrag' },
      object:   { score: recalcData.score.object,   max: 20, color: '#3FC0C0', label: 'Objekt' },
      location: { score: recalcData.score.location, max: 10, color: '#0B5C52', label: 'Lage' },
    };
  } else {
    subscores = approximateSubscores(baseline, signals);
  }

  return {
    deal, raw, market: effectiveMarket, signals, baseline, subscores,
    score: recalcData?.score || null, recalc: recalcData || null,
    isLoading, isError: false,
    userID,
  };
}
